import type { DocEntityType } from "../doc/docEntityTypes";

export enum DocEntityPrefix {
  Annotation = "annotation",
  AutoDimension = "autoDimension",
  Block = "block",
  Casework = "casework",
  Ceiling = "ceiling",
  Column = "column",
  DataTable = "dataTable",
  DataSequence = "dataSequence",
  Preset = "preset",
  DocRef = "docRef",
  Dormer = "dormer",
  DrawingRefs = "drawingRefs",
  EditMode = "editMode",
  Edge = "edge",
  EdgeModifier = "edgeModifier",
  ElectricalDevice = "electricalDevice",
  ElectricalLayout = "electricalLayout",
  ElectricalRule = "electricalRule",
  CatalogItem = "catalogItem",
  EdgeBehavior = "edgeBehavior",
  WallPlaneFilter = "wallPlaneFilter",
  WallPlaneRule = "wallPlaneRule",
  WallPlaneTrim = "wallPlaneTrim",
  WallPlaneAssembly = "wallPlaneAssembly",
  Elevation = "elevation",
  ElevationView = "elevationView",
  Facade = "facade",
  FacadeSpanStrata = "facadeSpanStrata",
  FacadeSweepStrata = "facadeSweepStrata",
  Floor = "floor",
  FloorPerimeterOpening = "floorPerimeterOpening",
  FloorFinish = "floorFinish",
  Foundation = "foundation",
  FoundationAssembly = "foundationAssembly",
  FoundationComponentEntity = "foundationComponentEntity",
  FoundationSystem = "foundationSystem",
  HeaderAssembly = "headerAssembly",
  FramingComponent = "framingComponent",
  FrameFloorAssembly = "frameFloorAssembly",
  SlabFloorAssembly = "slabFloorAssembly",
  SlabComponent = "slabComponent",
  SheathingComponent = "sheathingComponent",
  MasonryComponent = "masonryComponent",
  CipComponent = "cipComponent",
  RoofOpening = "roofOpening",
  GeneratedEntity = "generatedEntity",
  Group = "group",
  Component = "component",
  ComponentInstance = "componentInstance",
  Level = "level",
  CompositeLevel = "compositeLevel",
  Measurement = "measurement",
  Modifier = "modifier",
  Opening = "opening",
  Option = "option",
  OptionEffect = "optionEffect",
  OptionLocks = "optionLocks",
  OptionRule = "optionRule",
  OptionSet = "optionSet",
  InstanceOptionSet = "instanceOptionSet",
  OptionSetOverrides = "optionSetOverrides",
  OptionTemplate = "optionTemplate",
  Palette = "palette",
  PlanView = "planView",
  Profile = "profile",
  PropInstance = "propInstance",
  Railing = "railing",
  RailingSetting = "railingSetting",
  RailingPost = "railingPost",
  FreeformWall = "freeformWall",
  ReferenceGeo = "referenceGeo",
  Roof = "roof",
  RoofSide = "roofSide",
  RoofSystem = "roofSystem",
  RoofVolume = "roofVolume",
  Room = "room",
  RoomLayout = "roomLayout",
  RoomLayoutInstance = "roomLayoutInstance",
  SectionView = "sectionView",
  Settings = "settings",
  SettingsSet = "settingsSet",
  EntitySettings = "entitySettings",
  HandleBasedSelector = "handleBasedSelector",
  Shape = "shape",
  Sheet = "sheet",
  Site = "site",
  SiteShape = "siteShape",
  Skylight = "skylight",
  StairBlock = "stairBlock",
  StairSetting = "stairSetting",
  StairSystem = "stairSystem",
  Underlay = "underlay",
  Asset = "asset",
  Vertex = "vertex",
  View3D = "view3D",
  PlanSetTemplate = "planSetTemplate",
  PlanSetTemplateConfig = "planSetTemplateConfig",
  ContentTemplate = "contentTemplate",
  ContentTemplateInstance = "contentTemplateInstance",
  ContentReplicator = "contentReplicator",
  Variable = "variable",
  GlobalParameter = "globalParameter",
  SystemParameter = "systemParameter",
  ViewOverride = "viewOverride",
  Viewport = "viewport",
  ViewSelector = "viewSelector",
  ViewTemplate = "viewTemplate",
  ViewTemplateOverride = "viewTemplateOverride",
  Label = "label",
  ReportableUnitDesignation = "reportableUnitDesignation",
  Effect = "effect",
  Datum = "datum",
  RuleBasedSelector = "ruleBasedSelector",
  RuleBasedSelectorInstance = "ruleBasedSelectorInstance",
  PropAttributes = "propAttributes",
  IndeterminateId = "indeterminateId",
  FloorSystem = "floorSystem",
  FloorBay = "floorBay",
  FloorSplitLine = "splitLine",
}

export const DocEntityPrefixToDocEntityTypes: {
  [key in DocEntityPrefix]: DocEntityType[];
} = {
  [DocEntityPrefix.Annotation]: ["ANNOTATION"],
  [DocEntityPrefix.AutoDimension]: ["AUTO_DIMENSION"],
  [DocEntityPrefix.Block]: ["BLOCK"],
  [DocEntityPrefix.Casework]: ["CASEWORK"],
  [DocEntityPrefix.CatalogItem]: ["CATALOG_ITEM"],
  [DocEntityPrefix.Preset]: ["PRESET"],
  [DocEntityPrefix.Ceiling]: ["CEILING"],
  [DocEntityPrefix.Column]: ["COLUMN"],
  [DocEntityPrefix.DataTable]: ["DATA_TABLE"],
  [DocEntityPrefix.DataSequence]: ["DATA_SEQUENCE"],
  [DocEntityPrefix.DocRef]: ["DOC_REF"],
  [DocEntityPrefix.Dormer]: ["DORMER"],
  [DocEntityPrefix.DrawingRefs]: ["DRAWING_REF"],
  [DocEntityPrefix.EditMode]: ["EDIT_MODE"],
  [DocEntityPrefix.Edge]: ["EDGE"],
  [DocEntityPrefix.EdgeBehavior]: ["EDGE_BEHAVIOR"],
  [DocEntityPrefix.WallPlaneFilter]: ["WALL_PLANE_FILTER"],
  [DocEntityPrefix.WallPlaneRule]: ["WALL_PLANE_RULE"],
  [DocEntityPrefix.WallPlaneTrim]: ["WALL_PLANE_TRIM"],
  [DocEntityPrefix.WallPlaneAssembly]: ["WALL_PLANE_ASSEMBLY"],
  [DocEntityPrefix.EdgeModifier]: ["EDGE_MODIFIER"],
  [DocEntityPrefix.ElectricalDevice]: ["ELECTRICAL_DEVICE"],
  [DocEntityPrefix.ElectricalLayout]: ["ELECTRICAL_LAYOUT"],
  [DocEntityPrefix.ElectricalRule]: ["ELECTRICAL_RULE"],
  [DocEntityPrefix.Elevation]: ["ELEVATION"],
  [DocEntityPrefix.ElevationView]: ["ELEVATION_VIEW"],
  [DocEntityPrefix.SectionView]: ["SECTION_VIEW"],
  [DocEntityPrefix.Facade]: ["FACADE", "FACADE_SPAN_STRATUM", "FACADE_SWEEP_STRATUM"], // GROUPABLE TYPE MUST GO FIRST IN THE LIST
  [DocEntityPrefix.FacadeSpanStrata]: ["FACADE_SPAN_STRATUM"],
  [DocEntityPrefix.FacadeSweepStrata]: ["FACADE_SWEEP_STRATUM"],
  [DocEntityPrefix.Floor]: ["FLOOR"],
  [DocEntityPrefix.FloorFinish]: ["FLOOR_FINISH"],
  [DocEntityPrefix.Foundation]: ["FOUNDATION"],
  [DocEntityPrefix.FoundationAssembly]: ["FOUNDATION_ASSEMBLY"],
  [DocEntityPrefix.FoundationComponentEntity]: ["FOUNDATION_COMPONENT"],
  [DocEntityPrefix.FoundationSystem]: ["FOUNDATION_SYSTEM"],
  [DocEntityPrefix.RoofOpening]: ["ROOF_OPENING"],
  [DocEntityPrefix.GeneratedEntity]: ["GENERATED_ENTITY"],
  [DocEntityPrefix.Group]: ["GROUP"],
  [DocEntityPrefix.Component]: ["COMPONENT"],
  [DocEntityPrefix.ComponentInstance]: ["COMPONENT_INSTANCE"],
  [DocEntityPrefix.Level]: ["LEVEL"],
  [DocEntityPrefix.CompositeLevel]: ["COMPOSITE_LEVEL"],
  [DocEntityPrefix.Measurement]: ["MEASUREMENT"],
  [DocEntityPrefix.Modifier]: ["MODIFIER"],
  [DocEntityPrefix.Opening]: ["OPENING"],
  [DocEntityPrefix.Option]: ["OPTION"],
  [DocEntityPrefix.OptionEffect]: ["OPTION_EFFECT"],
  [DocEntityPrefix.OptionLocks]: ["OPTION_LOCK"],
  [DocEntityPrefix.OptionRule]: ["OPTION_RULE"],
  [DocEntityPrefix.OptionSet]: ["OPTION_SET"],
  [DocEntityPrefix.InstanceOptionSet]: ["INSTANCE_OPTION_SET"],
  [DocEntityPrefix.OptionSetOverrides]: ["OPTION_SET_OVERRIDES"],
  [DocEntityPrefix.OptionTemplate]: ["OPTION_TEMPLATE"],
  [DocEntityPrefix.Palette]: ["PALETTE"],
  [DocEntityPrefix.PlanView]: ["PLAN_VIEW"],
  [DocEntityPrefix.Profile]: ["PROFILE"],
  [DocEntityPrefix.PropInstance]: ["PROP_INSTANCE"],
  [DocEntityPrefix.Railing]: ["RAILING"],
  [DocEntityPrefix.RailingSetting]: ["RAILING_SETTING"],
  [DocEntityPrefix.RailingPost]: ["RAILING_POST"],
  [DocEntityPrefix.FreeformWall]: ["FREEFORM_WALL"],
  [DocEntityPrefix.ReferenceGeo]: ["REFERENCE_GEOM"],
  [DocEntityPrefix.Roof]: ["ROOF"],
  [DocEntityPrefix.RoofSide]: ["ROOF_SIDE"],
  [DocEntityPrefix.RoofSystem]: ["ROOF_SYSTEM"],
  [DocEntityPrefix.RoofVolume]: ["ROOF_VOLUME"],
  [DocEntityPrefix.Room]: ["ROOM"],
  [DocEntityPrefix.RoomLayout]: ["ROOM_LAYOUT"],
  [DocEntityPrefix.RoomLayoutInstance]: ["ROOM_LAYOUT_INSTANCE"],
  [DocEntityPrefix.Settings]: ["SETTINGS"],
  [DocEntityPrefix.SettingsSet]: ["SETTINGS_SET"],
  [DocEntityPrefix.EntitySettings]: ["ENTITY_SETTINGS"],
  [DocEntityPrefix.HandleBasedSelector]: ["SELECTOR"],
  [DocEntityPrefix.Shape]: ["SHAPE"],
  [DocEntityPrefix.Sheet]: ["SHEET"],
  [DocEntityPrefix.Site]: ["SITE"],
  [DocEntityPrefix.SiteShape]: ["SITE_SHAPE"],
  [DocEntityPrefix.Skylight]: ["SKYLIGHT"],
  [DocEntityPrefix.StairBlock]: ["STAIR_BLOCK"],
  [DocEntityPrefix.StairSetting]: ["STAIR_SETTING"],
  [DocEntityPrefix.StairSystem]: ["STAIR_SYSTEM"],
  [DocEntityPrefix.Underlay]: ["UNDERLAY"],
  [DocEntityPrefix.Asset]: ["ASSET"],
  [DocEntityPrefix.Vertex]: ["VERTEX", "EDGE"],
  [DocEntityPrefix.View3D]: ["VIEW_3D"],
  [DocEntityPrefix.PlanSetTemplate]: ["PLAN_SET_TEMPLATE"],
  [DocEntityPrefix.ContentTemplate]: ["CONTENT_TEMPLATE"],
  [DocEntityPrefix.ContentTemplateInstance]: ["CONTENT_TEMPLATE_INSTANCE"],
  [DocEntityPrefix.ContentReplicator]: ["CONTENT_REPLICATOR"],
  [DocEntityPrefix.Variable]: ["LOCAL_PARAMETER"],
  [DocEntityPrefix.GlobalParameter]: ["GLOBAL_PARAMETER"],
  [DocEntityPrefix.SystemParameter]: ["SYSTEM_PARAMETER"],
  [DocEntityPrefix.ViewOverride]: ["VIEW_OVERRIDE"],
  [DocEntityPrefix.Viewport]: ["VIEWPORT"],
  [DocEntityPrefix.ViewSelector]: ["VIEW_SELECTOR"],
  [DocEntityPrefix.ViewTemplate]: ["VIEW_TEMPLATE"],
  [DocEntityPrefix.ViewTemplateOverride]: ["VIEW_TEMPLATE_OVERRIDE"],
  [DocEntityPrefix.PlanSetTemplateConfig]: ["PLAN_SET_TEMPLATE_CONFIG"],
  [DocEntityPrefix.Label]: ["LABEL"],
  [DocEntityPrefix.ReportableUnitDesignation]: ["REPORTABLE_UNIT_DESIGNATION"],
  [DocEntityPrefix.Effect]: ["EFFECT"],
  [DocEntityPrefix.Datum]: ["DATUM"],
  [DocEntityPrefix.RuleBasedSelector]: ["RULE_BASED_SELECTOR"],
  [DocEntityPrefix.RuleBasedSelectorInstance]: ["RULE_BASED_SELECTOR_INSTANCE"],
  [DocEntityPrefix.PropAttributes]: ["PROP"],
  [DocEntityPrefix.IndeterminateId]: ["INDETERMINATE_ENTITY_TYPE"],
  [DocEntityPrefix.FramingComponent]: ["FRAMING_COMPONENT"],
  [DocEntityPrefix.HeaderAssembly]: ["HEADER_ASSEMBLY"],
  [DocEntityPrefix.FrameFloorAssembly]: ["FRAME_FLOOR_ASSEMBLY"],
  [DocEntityPrefix.SlabFloorAssembly]: ["SLAB_FLOOR_ASSEMBLY"],
  [DocEntityPrefix.SlabComponent]: ["SLAB_COMPONENT"],
  [DocEntityPrefix.SheathingComponent]: ["SHEATHING_COMPONENT"],
  [DocEntityPrefix.MasonryComponent]: ["MASONRY_COMPONENT"],
  [DocEntityPrefix.CipComponent]: ["CIP_COMPONENT"],
  [DocEntityPrefix.FloorSystem]: ["FLOOR_SYSTEM"],
  [DocEntityPrefix.FloorPerimeterOpening]: ["FLOOR_PERIMETER_OPENING"],
  [DocEntityPrefix.FloorBay]: ["FLOOR_BAY"],
  [DocEntityPrefix.FloorSplitLine]: ["FLOOR_SPLIT_LINE"],
};
